/**
 * Shared data table + toolbar surface styles.
 *
 * HEIGHT CONTRACT — every interactive element on a table toolbar
 * (buttons, search bar, time picker trigger, load more) uses h-8 (32px).
 * Table rows (data + header) also use h-8.
 * This matches the sidebar nav-item height so the whole UI reads at one cadence.
 */

export const TABLE_STYLES = {
  primaryBg:        'var(--surface-base)',
  secondaryBg:      'var(--surface-sunken)',
  textPrimary:      'var(--text-primary)',
  textSecondary:    'var(--text-muted)',
  textWarmSecondary:'var(--text-muted)',
  accentcolor:      'var(--text-primary)',
  borderColor:      'var(--border-default)',
  headerBg:         'var(--surface-sunken)',
  headerText:       'var(--text-muted)',
  pageBg:           'var(--surface-base)',
};

/**
 * Toolbar buttons — Show Filter, Edit Columns, etc.
 * h-8 = 32px, exactly matches sidebar nav-item height.
 */
/**
 * Keyboard focus ring for the toolbar controls.
 *
 * None of these classes drew one, so every toolbar button across Activity,
 * Agent runs, Spans and Issues was tab-reachable and fell back to the UA
 * default — which in Chrome is a dark outline that all but disappears against
 * the dark surface. `--state-focus-ring` carries a per-theme alpha and is what
 * the rest of the app already uses.
 */
export const dataTableFocusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

export const activityToolbarButtonClass =
  `inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover whitespace-nowrap ${dataTableFocusRing}`;

/**
 * Search / input bar wrapper — same h-8.
 */
export const activitySearchWrapperClass =
  'inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3';

/**
 * Text inside search input.
 */
export const activitySearchInputClass =
  'w-full border-0 bg-transparent text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] outline-none focus:outline-none focus:ring-0';

/**
 * Load More / neutral CTAs — same h-8.
 */
export const activityNeutralButtonClass =
  `inline-flex h-8 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface px-4 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed ${dataTableFocusRing}`;

/**
 * Table header cell — h-8 row, left-aligned, sentence case (labels keep their
 * own capitalization; no uppercase transform).
 */
export const tableHeaderCellClass =
  'h-8 px-3 text-left text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)] whitespace-nowrap';

/**
 * Table data cell — left-aligned. h-8 row height enforced on the <tr>.
 */
export const tableDataCellClass =
  'px-3 text-left align-middle text-[12px] text-[color:var(--text-primary)]';

/**
 * Table row — h-8 enforced, subtle divider that works in both modes.
 */
export const tableRowClass =
  'h-8 cursor-pointer border-b border-[color:var(--border-subtle)] last:border-b-0 hover:bg-surface-hover transition-colors';

/**
 * Table header row.
 *
 * No fill. `surface-sunken` is #f5f5f5 against a #ffffff card — a 4% step that
 * reads as a smudge rather than a deliberate band, and it made every table in
 * light mode look dirty. Structure comes from the bottom border instead, and
 * the header earns its rank from the uppercase micro-label above (the same
 * treatment section headings already use), which works identically in both
 * themes.
 */
export const tableHeaderRowClass =
  'h-8 border-b border-[color:var(--border-default)]';

/**
 * Detail drawer panel — the Events and Sessions popups.
 *
 * Same shell as AgentRunDetailPage's non-contained panel so a detail popup is
 * the same object wherever it opens from. The two Activity drawers previously
 * used `w-[min(85vw,720px)]` while the run drawer used `w-[min(92vw,1000px)]`,
 * which is why switching between them felt like two different apps.
 */
export const ACTIVITY_DRAWER_PANEL_CLASS =
  'pointer-events-auto relative flex h-full w-[min(92vw,880px)] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl';

/**
 * Icon-only control in a drawer's top row (prev / next / close). h-7, matching
 * the run drawer's controls.
 */
export const drawerIconButtonClass =
  `flex h-7 w-7 flex-none items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-30 ${dataTableFocusRing}`;

/**
 * Popup / modal row item (e.g. Edit Columns list item) — same h-8.
 */
export const popupRowItemClass =
  'flex h-8 w-full items-center gap-2 rounded-md px-3 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover cursor-pointer';

export default TABLE_STYLES;
